// Attune Experience end-mode: turn a finished diagnostic + the learner's chosen
// modality into a short, personalised mini-lesson — a 3-beat arc rendered
// natively on the course theme:
//
//   1. Frame      — a personalised text slide setting up what they'll work on + why
//   2. Practise   — a real interactive of the chosen modality's best-fit type
//   3. Consolidate — a reflection prompt tying it back to their real situation
//
// Reuses the existing back-end rather than inventing a parallel one:
//   - modality → interaction type:  MODALITIES[key].cluster (ranked best-fit)
//   - the type's data shape:         DATA_CONTRACTS[typeId] (the Builder's contract)
//   - the autonomous quality gate:   validateSlideData (mechanical, one repair pass)
//   - JSON → native Slide:           transformDesignerJSON (the Lesson Designer importers)
//
// Every model call goes through the metered /api/ai/attune/generate endpoint, so
// the build debits the credit ledger like every other AI feature. The interactive
// (Sonnet) and the arc text (Haiku) are generated in parallel — the arc is
// best-effort, so a single interactive still ships if the arc call fails.
//
// The PROMPTS for every call live on the server (lib/attune/prompts.ts) and are
// selected by the mode this module names; it sends only the learner's data
// (transcript, diagnostic, lesson/course context) and interprets what comes
// back. The types and helpers that both sides share are re-exported from
// there so existing imports keep working.

import type { Slide, TextSlideData, ReflectionSlideData } from '../../components/course-builder/slides/slideTypes';
import { SLIDE_REGISTRY } from '../../components/course-builder/slides/slideRegistry';
import { transformDesignerJSON, type DesignerJSON, type DesignerSlide } from '../lessonImport';
import { validateSlideData } from '../scenario-builder/validateSlideData';
import { MODALITY_KEYS, type ModalityKey, type InteractionTypeId } from '../scenario-builder/modality';
import { streamAttuneChat, streamAttuneGenerate } from './streamClient';
import {
  READY_MARKER,
  RUNTIME_UNBUILDABLE,
  pickInteractionType,
  type ExtractedDiagnostic,
  type ExperiencePromptInput,
} from './prompts';

export { READY_MARKER, RUNTIME_UNBUILDABLE, pickInteractionType, type ExtractedDiagnostic };

// Strip inline markdown emphasis the model slips into text fields (**bold**,
// *italic*, __bold__, `code`). The slide renderers don't parse markdown, so these
// markers render literally as clutter — removing them is strictly an improvement.
export function stripInlineMarkdown(s: string): string {
  return s
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}

/** Split a completed reply into learner-facing text + the readiness signal. */
export function stripReadyMarker(s: string): { text: string; ready: boolean } {
  const ready = s.trimEnd().endsWith(READY_MARKER);
  return {
    ready,
    text: ready ? s.trimEnd().slice(0, s.trimEnd().length - READY_MARKER.length).trimEnd() : s,
  };
}

/** While a reply is still streaming, hide any trailing PARTIAL marker so the
 *  learner never sees "[REA" flash before the close lands. */
export function trimStreamingMarker(s: string): string {
  for (let n = Math.min(READY_MARKER.length, s.length); n > 0; n--) {
    if (s.endsWith(READY_MARKER.slice(0, n))) return s.slice(0, s.length - n).trimEnd();
  }
  return s;
}

/** A diagnostic conversation that yielded too little to personalise from —
 *  e.g. a handful of one-word answers. The wrap-up must not perform certainty
 *  it doesn't have (no quote panel, no trip-up reveal). */
export function isThinTranscript(turns: { role: string; content: string }[]): boolean {
  const answers = turns.filter((t) => t.role === 'user');
  const words = answers.reduce(
    (n, t) => n + t.content.trim().split(/\s+/).filter(Boolean).length,
    0,
  );
  return answers.length === 0 || words < 30;
}

/**
 * Normalise raw model output before validation + import:
 *   1. With a permissive tool schema the model sometimes JSON-encodes nested
 *      values (e.g. `branches: "[{...}]"` instead of a real array), which fails
 *      the importer's `Array.isArray` checks → parse those back into real values.
 *   2. The model also slips markdown emphasis into prose fields → strip it.
 */
function normalizeGenerated(value: unknown): unknown {
  if (typeof value === 'string') {
    const t = value.trim();
    if ((t.startsWith('[') && t.endsWith(']')) || (t.startsWith('{') && t.endsWith('}'))) {
      try {
        return normalizeGenerated(JSON.parse(t));
      } catch {
        return stripInlineMarkdown(value);
      }
    }
    return stripInlineMarkdown(value);
  }
  if (Array.isArray(value)) return value.map(normalizeGenerated);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = normalizeGenerated(v);
    return out;
  }
  return value;
}

export interface ExperienceInput {
  modality: ModalityKey;
  transcript: { role: 'user' | 'assistant'; content: string }[];
  topic?: string;
  audience?: 'adult' | 'high-school';
  /** Whole-lesson text, so the build is grounded in what the learner just did. */
  lessonContext?: string;
  /** Prior course sections (horizon-bounded), so an end-of-course experience can
   *  synthesise the whole journey, not just the final lesson. */
  courseContext?: string;
  /** Where this moment sits. 'hook' = a brief attention-grabbing warm-up BEFORE
   *  the lesson; 'experience' (default) = an in-depth apply/extend at the end. */
  position?: 'hook' | 'experience';
  /** Structured diagnostic — sharpens the build around the learner's real gap. */
  diagnostic?: ExtractedDiagnostic;
  /** The learner's already-strong area, surfaced by the end-of-lesson wrap-up.
   *  Powers "both doors" — for a confident learner the build stretches this
   *  strength instead of drilling a gap they don't have. Experience only. */
  strength?: string;
  /** When false, build ONLY the interactive — no framing intro / reflection
   *  slides. The end-experience "quick" route uses this so a time-pressed learner
   *  gets a genuinely short option. Defaults to true (full frame → do → reflect). */
  includeArc?: boolean;
}

export interface ExperienceResult {
  slides: Slide[];
  interactionType: InteractionTypeId;
  warnings: string[];
  generationId?: string;
}

/** The prompt-relevant half of an ExperienceInput — what the build and arc
 *  modes are composed from. Nothing else (e.g. includeArc) crosses the wire. */
function promptInput(input: ExperienceInput): ExperiencePromptInput {
  return {
    modality: input.modality,
    transcript: input.transcript,
    topic: input.topic,
    audience: input.audience,
    lessonContext: input.lessonContext,
    courseContext: input.courseContext,
    position: input.position,
    diagnostic: input.diagnostic,
    strength: input.strength,
  };
}

function toSlides(typeId: InteractionTypeId, data: Record<string, unknown>): { slides: Slide[]; warnings: string[] } {
  // The importer dispatches on the registry's *designer label* (e.g.
  // scenario-simulation → 'SCENARIO-SIM'), which is NOT the uppercased template
  // id — uppercasing silently fell back to an empty Text slide. Look it up.
  const designerLabel =
    SLIDE_REGISTRY.find((e) => e.template === typeId)?.designer?.label ?? typeId.toUpperCase();
  const slide = {
    n: 1,
    color: '#10b981',
    label: designerLabel,
    text: '',
    image: null,
    options: null,
    pairs: null,
    note: null,
    data: { template: typeId, ...data },
  } as unknown as DesignerSlide;

  const json: DesignerJSON = {
    title: 'Your personalised experience',
    code: '',
    total: 1,
    time: '~3 min',
    headings: [{ title: '', sections: [{ title: 'Experience', slides: [slide] }] }],
  };

  const out = transformDesignerJSON(json, 'modern-light');
  const slides = out.lessons.flatMap((l) => l.content.slides);
  return { slides, warnings: out.warnings };
}

/**
 * Build the personalised interactive (the middle beat). Generates the interaction
 * JSON single-shot against the type's contract, runs one repair pass if the
 * mechanical validator finds critical issues, then imports it to a native Slide.
 */
async function buildInteractive(
  typeId: InteractionTypeId,
  input: ExperienceInput,
  opts?: { signal?: AbortSignal },
): Promise<{ slides: Slide[]; warnings: string[]; generationId?: string }> {
  // The server picks the same typeId from the modality (pickInteractionType)
  // and composes the contract-bearing system prompt around it.
  const first = await streamAttuneGenerate({
    mode: 'build',
    data: promptInput(input),
    signal: opts?.signal,
  });

  let data = normalizeGenerated(first.data) as Record<string, unknown>;
  let generationId = first.generationId;
  let result = validateSlideData(data, typeId);

  // One repair pass — feed the critical issues back and re-emit. The validator
  // is the autonomous quality gate here (no human/QA loop at runtime).
  if (!result.ok) {
    const issues = result.criticalIssues
      .map((i) => `- ${i.location}: ${i.issue} — ${i.fixInstruction}`)
      .join('\n');
    const repair = await streamAttuneGenerate({
      mode: 'build_repair',
      data: { ...promptInput(input), previousData: data, issues },
      signal: opts?.signal,
    });
    const repairedData = normalizeGenerated(repair.data) as Record<string, unknown>;
    const repaired = validateSlideData(repairedData, typeId);
    // Keep whichever is structurally valid; prefer the repair when it's clean.
    if (repaired.ok || !result.ok) {
      data = repairedData;
      generationId = repair.generationId ?? generationId;
      result = repaired;
    }
  }

  const { slides, warnings } = toSlides(typeId, data);
  return {
    slides,
    warnings: [...warnings, ...result.warnings.map((w) => `${w.location}: ${w.issue}`)],
    generationId,
  };
}

export interface ArcText {
  intro: { heading: string; body: string };
  reflection: { prompt: string };
}

/**
 * Generate the framing (before) + reflection (after) that wrap the interactive,
 * turning a single interaction into a short mini-lesson. Cheap (Haiku), typed
 * schema, and best-effort — the caller drops the arc and ships just the
 * interactive if this fails.
 */
async function generateArc(
  input: ExperienceInput,
  opts?: { signal?: AbortSignal },
): Promise<ArcText | null> {
  // The server derives the interaction type (and its name, for the framing)
  // from the modality, exactly as pickInteractionType does here.
  const res = await streamAttuneGenerate({
    mode: 'arc',
    data: promptInput(input),
    signal: opts?.signal,
  });
  const d = normalizeGenerated(res.data) as { introHeading?: unknown; introBody?: unknown; reflectionPrompt?: unknown };
  const heading = typeof d.introHeading === 'string' ? d.introHeading.trim() : '';
  const body = typeof d.introBody === 'string' ? d.introBody.trim() : '';
  const prompt = typeof d.reflectionPrompt === 'string' ? d.reflectionPrompt.trim() : '';
  if (!body && !prompt) return null;
  return { intro: { heading, body }, reflection: { prompt } };
}

const escapeHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const textToHtml = (text: string): string =>
  text
    .split(/\n{2,}/)
    .map((p) => `<p>${escapeHtml(p.trim())}</p>`)
    .join('');

function introSlide(intro: { heading: string; body: string }): Slide {
  return {
    id: crypto.randomUUID(),
    order: 0,
    data: {
      template: 'text',
      layout: 'body',
      heading: intro.heading,
      body: textToHtml(intro.body),
      alignment: 'left',
    } as TextSlideData,
  };
}

function reflectionSlide(reflection: { prompt: string }): Slide {
  return {
    id: crypto.randomUUID(),
    order: 0,
    data: {
      template: 'reflection',
      heading: 'Make it yours',
      questions: [{ id: crypto.randomUUID(), prompt: reflection.prompt, placeholder: 'Write a sentence or two…' }],
    } as ReflectionSlideData,
  };
}

// Structured diagnostic extraction (ported from vibe-learning §A2). One cheap
// Haiku call → the five fields, anchored entirely to what the learner said.
export async function extractDiagnostic(
  input: { transcript: ExperienceInput['transcript']; audience?: ExperienceInput['audience']; lessonContext?: string },
  opts?: { signal?: AbortSignal },
): Promise<ExtractedDiagnostic> {
  const res = await streamAttuneGenerate({
    mode: 'diagnose',
    data: { transcript: input.transcript, audience: input.audience, lessonContext: input.lessonContext },
    signal: opts?.signal,
  });
  const d = normalizeGenerated(res.data) as Partial<Record<keyof ExtractedDiagnostic, unknown>>;
  const sm = typeof d.suggestedModality === 'string' && (MODALITY_KEYS as readonly string[]).includes(d.suggestedModality)
    ? (d.suggestedModality as ModalityKey)
    : 'scenario';
  return {
    learningFocus: String(d.learningFocus ?? ''),
    realContext: String(d.realContext ?? ''),
    keyGap: String(d.keyGap ?? ''),
    suggestedModality: sm,
    motivationHook: String(d.motivationHook ?? ''),
  };
}

/**
 * The reflection-back (ported from vibe-learning §A3) — the "here's what I heard"
 * moment that makes the diagnostic feel insightful, not transactional. Streams its
 * text via onText so the learner watches it form.
 */
export async function generateReflection(
  input: { transcript: ExperienceInput['transcript']; diagnostic: ExtractedDiagnostic; audience?: ExperienceInput['audience'] },
  onText?: (delta: string) => void,
  opts?: { signal?: AbortSignal },
): Promise<string> {
  const res = await streamAttuneChat({
    mode: 'reflection',
    data: { transcript: input.transcript, diagnostic: input.diagnostic, audience: input.audience },
    onText,
    signal: opts?.signal,
  });
  return res.fullText.trim();
}

// ─── End-of-lesson reflective wrap-up (Dylan's restructure) ─────────────────
// Instead of a blunt "here's what I heard" paragraph, the end experience mirrors
// the learner's own thinking back as a structured wrap-up — their words, a
// strength, the real gap, how they like to learn, the one thing likely to trip
// them up — so the moment feels like being *read*, not assigned more work. It is
// the same single cheap Haiku call the old reflection was; only the shape is
// richer (so cost is a few hundred extra output tokens, no second build). The
// learner then chooses a route, and the (Sonnet) build runs as before.

export interface AttuneWrapup {
  /** ≤ 12 words — how THEY approach the subject ("how you think about money"). */
  openingHeadline: string;
  /** 1-2 sentences, no score / no right-answers; may greet by name. */
  openingLead: string;
  /** The most revealing thing they said, in their own voice (≤ ~30 words). */
  quote: string;
  /** The interpretation that lands — "you don't have an X problem, you have Y." */
  quoteReading: string;
  strengthTitle: string;
  strengthBody: string;
  gapTitle: string;
  gapBody: string;
  /** The two ends of how they like to learn + how far toward the right (0-100). */
  meterLeft: string;
  meterRight: string;
  meterValue: number;
  /** Exactly three short reads on them (motivation / how they learn / preference). */
  chips: string[];
  /** One pattern likely to trip them up, named kindly and pointed forward. */
  tripupTitle: string;
  tripupBody: string;
}

/** Fallback wrap-up assembled from the structured diagnostic — so the panel
 *  always has something true to show if the richer generation fails. */
export function wrapupFromDiagnostic(d: ExtractedDiagnostic): AttuneWrapup {
  return {
    openingHeadline: 'Here’s what came through',
    openingLead: 'No score, no right answers — just what surfaced while we talked.',
    quote: d.learningFocus || 'what you’re really after here',
    quoteReading: d.realContext || 'There’s more underneath what you first named.',
    strengthTitle: 'What you brought',
    strengthBody: d.motivationHook || 'You showed up with a real reason to get this — that’s the fuel.',
    gapTitle: 'The real gap',
    gapBody: d.keyGap || 'The distance between where you are and where you want to be.',
    meterLeft: 'Show me the principle',
    meterRight: 'Show me what to do',
    meterValue: 60,
    chips: [d.motivationHook, d.learningFocus, d.keyGap].map((s) => (s || '').trim()).filter(Boolean).slice(0, 3),
    tripupTitle: 'One thing to watch',
    tripupBody: d.keyGap || 'Keep an eye on the gap we named as you go.',
  };
}

/**
 * Generate the reflective wrap-up from the diagnostic conversation. One cheap
 * Haiku call, typed schema. NEVER throws — falls back to a diagnostic-derived
 * wrap-up so the end-of-lesson moment always renders.
 */
export async function generateWrapup(
  input: {
    transcript: ExperienceInput['transcript'];
    diagnostic: ExtractedDiagnostic;
    audience?: ExperienceInput['audience'];
    lessonContext?: string;
    learnerName?: string;
  },
  opts?: { signal?: AbortSignal },
): Promise<AttuneWrapup> {
  try {
    const res = await streamAttuneGenerate({
      mode: 'wrapup',
      data: {
        transcript: input.transcript,
        diagnostic: input.diagnostic,
        audience: input.audience,
        lessonContext: input.lessonContext,
        learnerName: input.learnerName,
      },
      signal: opts?.signal,
    });
    const d = normalizeGenerated(res.data) as Partial<Record<keyof AttuneWrapup, unknown>>;
    const str = (v: unknown, fb = ''): string => (typeof v === 'string' ? v.trim() : fb);
    const fb = wrapupFromDiagnostic(input.diagnostic);
    const chips = Array.isArray(d.chips)
      ? d.chips.map((c) => str(c)).filter(Boolean).slice(0, 3)
      : [];
    const meter = typeof d.meterValue === 'number' && isFinite(d.meterValue)
      ? Math.max(0, Math.min(100, Math.round(d.meterValue)))
      : 50;
    const w: AttuneWrapup = {
      openingHeadline: str(d.openingHeadline),
      openingLead: str(d.openingLead),
      quote: str(d.quote),
      quoteReading: str(d.quoteReading),
      strengthTitle: str(d.strengthTitle, fb.strengthTitle),
      strengthBody: str(d.strengthBody, fb.strengthBody),
      gapTitle: str(d.gapTitle, fb.gapTitle),
      gapBody: str(d.gapBody, fb.gapBody),
      meterLeft: str(d.meterLeft, fb.meterLeft),
      meterRight: str(d.meterRight, fb.meterRight),
      meterValue: meter,
      chips: chips.length ? chips : fb.chips,
      tripupTitle: str(d.tripupTitle, fb.tripupTitle),
      tripupBody: str(d.tripupBody, fb.tripupBody),
    };
    // If the lead panels came back empty, the generation didn't really land —
    // use the diagnostic-derived wrap-up instead of a half-empty one.
    if (!w.openingHeadline && !w.quote && !w.quoteReading) return fb;
    if (!w.openingHeadline) w.openingHeadline = fb.openingHeadline;
    if (!w.openingLead) w.openingLead = fb.openingLead;
    if (!w.quote) w.quote = fb.quote;
    if (!w.quoteReading) w.quoteReading = fb.quoteReading;
    return w;
  } catch {
    return wrapupFromDiagnostic(input.diagnostic);
  }
}

export interface ExperienceBuild {
  /** The framing intro slide — resolves fast (Haiku arc); null if the arc failed. */
  frame: Promise<Slide | null>;
  /** The interactive (slow, Sonnet) + the consolidation reflection. Rejects if the build fails. */
  rest: Promise<{ slides: Slide[]; warnings: string[] }>;
  interactionType: InteractionTypeId;
}

/** A build started before the learner has committed to a route — both calls
 *  are in flight; nothing is assembled until adoptExperience decides how. */
export interface PrebuiltExperience {
  interactive: Promise<{ slides: Slide[]; warnings: string[]; generationId?: string }>;
  arc: Promise<ArcText | null>;
  typeId: InteractionTypeId;
  /** Cancel both in-flight calls — for when the learner picks a route that
   *  doesn't build (e.g. back to the conversation). */
  abort: () => void;
}

/**
 * Fire the build calls WITHOUT deciding the final shape. The end-of-lesson
 * wrap-up uses this speculatively: the learner spends 15–40s reading the
 * wrap-up panel before picking a route, and since every route builds the same
 * interactive from the same diagnostic, starting here hides most of the
 * Sonnet build time behind the reading. The arc always fires too (cheap
 * Haiku) so the full-arc route is also instant; adopt drops it when unused.
 */
export function prebuildExperience(
  input: ExperienceInput,
  opts?: { signal?: AbortSignal; arc?: boolean },
): PrebuiltExperience {
  const controller = new AbortController();
  opts?.signal?.addEventListener('abort', () => controller.abort(), { once: true });
  const signal = controller.signal;

  const typeId = pickInteractionType(input.modality);
  const arcP: Promise<ArcText | null> =
    (opts?.arc ?? true) ? generateArc(input, { signal }).catch(() => null) : Promise.resolve(null);
  const interactiveP = buildInteractive(typeId, input, { signal });
  // A speculative build may never be adopted (learner goes back to chat) —
  // pre-attach a no-op handler so the rejection isn't reported as unhandled.
  // The original promise still rejects for whoever adopts it.
  void interactiveP.catch(() => {});

  return { interactive: interactiveP, arc: arcP, typeId, abort: () => controller.abort() };
}

/**
 * Assemble a prebuilt experience into the shape the UI plays, deciding NOW
 * whether the surrounding arc is included (the route choice arrives after the
 * calls were fired). The arc is best-effort (frame may be null); the
 * interactive is the core, so `rest` rejects if it fails.
 */
export function adoptExperience(pre: PrebuiltExperience, includeArc: boolean): ExperienceBuild {
  const frame = includeArc
    ? pre.arc.then((arc) => (arc && (arc.intro.body || arc.intro.heading) ? introSlide(arc.intro) : null))
    : Promise.resolve<Slide | null>(null);

  const rest = Promise.all([pre.interactive, pre.arc]).then(([interactive, arc]) => {
    const slides: Slide[] = [...interactive.slides];
    if (includeArc && arc && arc.reflection.prompt) slides.push(reflectionSlide(arc.reflection));
    return { slides, warnings: interactive.warnings };
  });

  return { frame, rest, interactionType: pre.typeId };
}

/**
 * Start the personalised mini-lesson arc: frame → interactive → consolidate.
 *
 * Returns two promises so the UI can reveal the framing intro the moment it's
 * ready (~5s, Haiku) while the interactive (slow, Sonnet) builds behind it —
 * instead of one long spinner. The arc call yields BOTH the frame and the
 * reflection text; the reflection rides on `rest` so it lands after the
 * interactive. When the route was pre-decided (no speculation), the arc call
 * is skipped entirely for arc-less builds rather than fired-and-dropped.
 */
export function startExperience(
  input: ExperienceInput,
  opts?: { signal?: AbortSignal },
): ExperienceBuild {
  const withArc = input.includeArc !== false;
  return adoptExperience(
    prebuildExperience(input, { signal: opts?.signal, arc: withArc }),
    withArc,
  );
}

/**
 * Convenience wrapper: await the whole arc and return the full slide sequence.
 * (The live UI uses startExperience directly for progressive reveal.)
 */
export async function generateExperience(
  input: ExperienceInput,
  opts?: { signal?: AbortSignal },
): Promise<ExperienceResult> {
  const build = startExperience(input, opts);
  const [frame, rest] = await Promise.all([build.frame, build.rest]);
  return {
    slides: [...(frame ? [frame] : []), ...rest.slides],
    interactionType: build.interactionType,
    warnings: rest.warnings,
  };
}
